import type { GQL_ID, NestedFilter } from '@@partner:graphql/types'
import { prepareSlotsFilter } from '@@partner:helpers/slot'
import { DateRangeFilter } from '@@partner:types:app'
import type { DateRange } from 'rsuite/esm/DateRangePicker'

import React, { useCallback, useMemo, useState } from 'react'

import {
  Form,
  CheckPicker,
  DateRangePicker,
  Tag,
  TagPicker,
  Button,
  SelectPicker,
  Checkbox
} from 'rsuite'
import LegacySearchIcon from '@rsuite/icons/legacy/Search'

import '@components/common/CommonFilter.scss'

export type SlotsFiltersDateRangeType = DateRange | null

interface ZoneOption {
  label: string
  value: GQL_ID | ''
}

export interface SlotsFilterProps {
  onFilterChanged: (filters: NestedFilter) => void
  showBulkUpdate?: boolean
  onShowBulkUpdate?: (show: boolean) => void
  canBulkUpdate?: boolean
  // Zone filter props
  zoneOptions?: ZoneOption[]
  selectedZoneId?: GQL_ID | ''
  onZoneChange?: (zoneId: GQL_ID | '' | null) => void
  loadingZones?: boolean
}

export interface SlotsFiltersType {
  ts_start: DateRangeFilter
}

const defaultFilter: SlotsFiltersType = {
  ts_start: {
    from: undefined,
    to: undefined
  }
}

export default function SlotsFilter ({ 
  onFilterChanged,
  showBulkUpdate,
  onShowBulkUpdate,
  canBulkUpdate,
  zoneOptions,
  selectedZoneId,
  onZoneChange,
  loadingZones
}: SlotsFilterProps) {
  const [ state, setState ] = useState<SlotsFiltersType>(defaultFilter)

  const tsStartDateRangeValue = useMemo<SlotsFiltersDateRangeType>(
    () =>
      state.ts_start.from && state.ts_start.to
        ? [ new Date(state.ts_start.from), new Date(state.ts_start.to) ]
        : null,
    [ state ]
  )

  const handleDateRangeChange = useCallback(
    (dateType: string) =>
      (value: DateRange | null, _e: any): void => {
        const [ from, to ] = value ?? []
        setState((state) => ({
          ...state,
          [dateType]: {
            from: from?.getTime(),
            to: to?.getTime()
          }
        }))
      },
    []
  )

  const handleDateRangeClean = useCallback(
    (dateType: string) => (e: any) => {
      handleDateRangeChange(dateType)(null, e)
    },
    [ handleDateRangeChange ]
  )

  const handleFormSubmit = useCallback(() => {
    onFilterChanged(prepareSlotsFilter(state))
  }, [ onFilterChanged, state ])

  return (
    <Form
      formValue={state}
      className="CommonFilter"
      onSubmit={handleFormSubmit}
    >
      <div className="CommonFilter__Filters">
        {zoneOptions && onZoneChange && (
          <Form.Group>
            <SelectPicker
              data={zoneOptions}
              value={selectedZoneId ?? ''}
              onChange={onZoneChange}
              placeholder="Выберите зону доставки"
              searchable
              style={{ width: 280 }}
              cleanable={false}
              loading={loadingZones}
            />
          </Form.Group>
        )}
        <Form.Group>
          <DateRangePicker
            value={tsStartDateRangeValue}
            onChange={handleDateRangeChange('ts_start')}
            placeholder="Время начала слота"
            onClean={handleDateRangeClean('ts_start')}
            size="sm"
            format="dd.MM.yyyy HH:mm"
            showMeridiem={false}
          />
        </Form.Group>
      </div>
      <div className="CommonFilter__Controls">
        <Button type="submit" size="lg" color="green" appearance="primary">
          <LegacySearchIcon /> Поиск
        </Button>
        {canBulkUpdate && onShowBulkUpdate && (
          <Checkbox
            checked={showBulkUpdate}
            onChange={() => onShowBulkUpdate(!showBulkUpdate)}
          >
            Массовое обновление
          </Checkbox>
        )}
      </div>
    </Form>
  )
}
